import React, { useState, useMemo } from 'react';

const TYPE_FILTERS = [
    { id: 'all', label: 'All', icon: '📋' },
    { id: 'task', label: 'Tasks', icon: '✓' },
    { id: 'application', label: 'Uni Apps', icon: '🎓' },
    { id: 'visa', label: 'Visa', icon: '✈️' },
    { id: 'agreement', label: 'Agreements', icon: '📄' },
    { id: 'case', label: 'Cases', icon: '📁' },
];

const URGENCY_FILTERS = [
    { id: 'all', label: 'All', color: 'gray' },
    { id: 'overdue', label: 'Overdue', color: 'red' },
    { id: 'critical', label: 'Critical', color: 'red' },
    { id: 'urgent', label: 'Urgent', color: 'yellow' },
    { id: 'attention', label: 'Attention', color: 'orange' },
];

const CriticalAlerts = ({
    alerts,
    loading,
    showAcknowledged,
    onToggleAcknowledged,
    onAcknowledge,
    onView,
    onAcknowledgeAll
}) => {
    const [typeFilter, setTypeFilter] = useState('all');
    const [urgencyFilter, setUrgencyFilter] = useState('all');

    // Filter alerts based on selected filters
    const filteredAlerts = useMemo(() => {
        return (alerts || []).filter(alert => {
            // Type filter
            if (typeFilter !== 'all') {
                const alertType = alert.type === 'university_application' ? 'application' : alert.type;
                if (alertType !== typeFilter) return false;
            }

            // Urgency filter
            if (urgencyFilter !== 'all') {
                if (urgencyFilter === 'overdue') {
                    if (!alert.overdue && !(alert.date && alert.date < new Date())) return false;
                } else if (alert.alertLevel !== urgencyFilter) {
                    return false;
                }
            }

            return true;
        });
    }, [alerts, typeFilter, urgencyFilter]);

    // Count alerts by type
    const typeCounts = useMemo(() => {
        const counts = { all: alerts?.length || 0 };
        (alerts || []).forEach(a => {
            const type = a.type === 'university_application' ? 'application' : a.type;
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [alerts]);

    const getIconForType = (type) => {
        switch (type) {
            case 'university_application':
            case 'application':
                return <span className="text-lg" role="img" aria-label="university">🎓</span>;
            case 'agreement':
                return <span className="text-lg">📄</span>;
            case 'visa':
            case 'visa_application':
                return <span className="text-lg">✈️</span>;
            case 'case':
                return <span className="text-lg">📁</span>;
            default:
                return <span className="text-lg">✓</span>;
        }
    };

    const getDaysUntil = (date) => {
        if (!date) return null;
        const now = new Date();
        const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const getUrgencyBadge = (alert) => {
        const days = getDaysUntil(alert.date);
        const isOverdue = alert.overdue || (days !== null && days < 0);

        if (isOverdue) {
            const overdueDays = days !== null ? Math.abs(days) : '?';
            return { label: `${overdueDays}d overdue`, color: 'bg-red-600 text-white' };
        }
        if (alert.alertLevel === 'critical' || (days !== null && days <= 1)) {
            return { label: days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`, color: 'bg-red-900/60 text-red-300' };
        }
        if (alert.alertLevel === 'urgent' || (days !== null && days <= 3)) {
            return { label: `${days}d left`, color: 'bg-yellow-900/60 text-yellow-300' };
        }
        if (days !== null) {
            return { label: `${days}d`, color: 'bg-gray-700 text-gray-300' };
        }
        return { label: 'ASAP', color: 'bg-orange-900/60 text-orange-300' };
    };

    if (loading) {
        return (
            <div className="bg-gray-800 rounded-lg border border-red-900/30 shadow-lg overflow-hidden h-64 animate-pulse">
                <div className="bg-red-900/20 p-4 border-b border-red-900/30 h-14"></div>
                <div className="p-4 space-y-4">
                    <div className="h-8 bg-gray-700 rounded w-full"></div>
                    <div className="h-8 bg-gray-700 rounded w-full"></div>
                    <div className="h-8 bg-gray-700 rounded w-full"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-800 rounded-lg border border-red-900/30 shadow-lg overflow-hidden h-full flex flex-col">
            {/* Header */}
            <div className="bg-red-900/20 p-4 border-b border-red-900/30 flex-shrink-0">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-red-400 font-bold uppercase tracking-wider flex items-center gap-2 text-sm sm:text-base">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                        Critical Alerts
                        <span className="text-xs bg-red-900/50 px-2 py-0.5 rounded-full">
                            {filteredAlerts.length}
                        </span>
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onToggleAcknowledged}
                            className="text-xs px-2 py-1 rounded bg-gray-800 border border-red-700 text-red-300 hover:bg-red-900/40"
                        >
                            {showAcknowledged ? 'Hide Ack.' : 'Show Ack.'}
                        </button>
                        {filteredAlerts.length > 0 && (
                            <button
                                onClick={onAcknowledgeAll}
                                className="text-xs px-2 py-1 rounded bg-gray-800 border border-red-700 text-red-300 hover:bg-red-900/40"
                            >
                                Ack. All
                            </button>
                        )}
                    </div>
                </div>

                {/* Type Filter Chips */}
                <div className="flex flex-wrap gap-1 mb-2">
                    {TYPE_FILTERS.map(filter => (
                        <button
                            key={filter.id}
                            onClick={() => setTypeFilter(filter.id)}
                            className={`px-2 py-1 text-xs rounded-full transition-colors ${typeFilter === filter.id
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                        >
                            {filter.icon} {filter.label}
                            {typeCounts[filter.id] > 0 && (
                                <span className="ml-1 opacity-75">({typeCounts[filter.id]})</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Urgency Filter */}
                <div className="flex flex-wrap gap-1">
                    {URGENCY_FILTERS.map(filter => (
                        <button
                            key={filter.id}
                            onClick={() => setUrgencyFilter(filter.id)}
                            className={`px-2 py-1 text-xs rounded transition-colors ${urgencyFilter === filter.id
                                    ? filter.color === 'red' ? 'bg-red-600 text-white'
                                        : filter.color === 'yellow' ? 'bg-yellow-600 text-white'
                                            : filter.color === 'orange' ? 'bg-orange-600 text-white'
                                                : 'bg-gray-600 text-white'
                                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Alert List */}
            <div className="flex-1 overflow-y-auto min-h-[200px]">
                {filteredAlerts.length > 0 ? (
                    <div className="divide-y divide-gray-700">
                        {filteredAlerts.map((alert) => {
                            const urgencyBadge = getUrgencyBadge(alert);
                            const isOverdue = alert.overdue || (alert.date && alert.date < new Date());

                            return (
                                <div
                                    key={`${alert.type}-${alert.id}`}
                                    className={`p-4 hover:bg-gray-700/50 transition-colors ${isOverdue ? 'bg-red-900/10' : ''}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        {/* Left: Icon and info */}
                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isOverdue ? 'bg-red-900/50' : 'bg-gray-700'
                                                }`}>
                                                {getIconForType(alert.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-white font-medium truncate">{alert.title}</span>
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${urgencyBadge.color}`}>
                                                        {urgencyBadge.label}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                                                    <span className="capitalize">{alert.type?.replace('_', ' ')}</span>
                                                    {alert.subtitle && <span>• {alert.subtitle}</span>}
                                                    {alert.date && (
                                                        <span className="text-gray-500">
                                                            • {alert.date.toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Actions */}
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                onClick={() => onView(alert)}
                                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => onAcknowledge(alert)}
                                                className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                                            >
                                                Ack
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-500 h-full flex flex-col items-center justify-center">
                        <div className="text-4xl mb-3">✅</div>
                        <div>No critical alerts</div>
                        {typeFilter !== 'all' || urgencyFilter !== 'all' ? (
                            <button
                                onClick={() => { setTypeFilter('all'); setUrgencyFilter('all'); }}
                                className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                            >
                                Clear filters
                            </button>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CriticalAlerts;
